'use client'

// frontend/src/context/BreadcrumbContext.tsx
//
// A page sets the short title of its own last crumb ("Hospital beds"), so the
// header can read "Command Briefing > Tender Explorer > Hospital beds" while
// the page title stays long. Until the page sets one, the header shows the
// route's own last segment (for a record, its reference).

import { createContext, useContext, useEffect, useMemo, useState } from 'react'

interface BreadcrumbContextValue {
  title: string | null
  setTitle: (title: string | null) => void
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null)

export function BreadcrumbProvider({ children }: { children: React.ReactNode }) {
  const [title, setTitle] = useState<string | null>(null)
  const value = useMemo(() => ({ title, setTitle }), [title])
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>
}

/** The title a page set for its last crumb, or null. Read by the header. */
export function useBreadcrumbTitle(): string | null {
  return useContext(BreadcrumbContext)?.title ?? null
}

/** Call from a page with its short title; cleared when the page unmounts. */
export function useSetBreadcrumbTitle(title: string | null | undefined): void {
  const context = useContext(BreadcrumbContext)
  const setTitle = context?.setTitle
  useEffect(() => {
    if (!setTitle) return
    setTitle(title?.trim() ? title.trim() : null)
    return () => setTitle(null)
  }, [setTitle, title])
}
